import * as React from 'react';
import { Warning } from 'rcd-cpp-ui/ui/alert';
import { useSelector } from '../../../utils/useSelector';
import { Role } from '../../../enums/Role';
import { RoleId } from '../../../enums/RoleId';
import { getAllRoles, isOwner } from '../../AccessControl/getPermission';
import { useConfigureWorkflowPermissions } from './useConfigureWorkflowPermissions';
import { useAlert } from '../../Alert/alert';
import { useLoader } from '../../Loader/loader';
import {
  ConfigureWorkflowWarningType,
  getConfigureWorkflowTypeEnums,
  getCreativeHelpDisabled,
  getDataValues,
  getInheritedContentTypeConfiguration,
  getSchemaFieldNameByField,
  getSkipWorkflowStatuses,
  getWarningMessages,
  isModified
} from '../../../utils';
import { WorkflowStep } from '../../../types/WorkflowStep';
import { WorkflowInfo } from '../../../types/WorkflowInfo';
import { ItemStatus, CreativeStatus } from '../../../enums/ItemStatus';
import { ConfigureWorkflowType } from '../../../enums/ConfigureWorkflowType';
import { ConfigureWorkflowTree } from './configureWorkflowTree';
import { Metadata } from '../../../types';
import {
  WorkflowStageUiConfigById,
  WorkflowSavePayload
} from '../../../types/ConfigureWorkflowTypes';

export { getDataValues };

export const defaultSkips = [
  ItemStatus.InitialEditingIntake,
  ItemStatus.Outline,
  ItemStatus.OutlineReview
];

type ConfigureMainWorkflowProps = {
  onClose: () => void;
  data: Partial<Metadata>;
  onSave: (payload: WorkflowSavePayload) => Promise<void>;
  workflowState: WorkflowSavePayload;
  onWorkflowStateChange: (update: Partial<WorkflowSavePayload>) => void;
  onSaveDisabledChange: (disabled: boolean) => void;
  errorMessage: string;
  onHasChangesChange: (hasChanges: boolean) => void;
  onIsModalDisabledChange: (disabled: boolean) => void;
  isSaving?: boolean;
};

export const ConfigureMainWorkflow = ({
  data,
  workflowState,
  onWorkflowStateChange,
  onSaveDisabledChange,
  errorMessage,
  onHasChangesChange,
  onIsModalDisabledChange,
  isSaving = false
}: ConfigureMainWorkflowProps) => {
  const {
    configureWorkflowSkipIds,
    configureWorkflowType,
    requestCreativeHelp,
    preProductionExternalReview,
    postProductionExternalReview
  } = workflowState;
  const empCd = useSelector(s => s.app.user.employeeId);
  const isAdmin = useSelector(s => s.app.user.roles?.includes(Role.admin));
  const isBackupTM = useSelector(s =>
    s.view?.backingUpFor?.roleIds?.includes(RoleId.teamManager)
  );
  const getAPI = useSelector(s => s.app.elFn.getAPI);
  const typeEnums = getConfigureWorkflowTypeEnums('configureWorkflowType');
  const schemas = useSelector(s => s.planning.selectedWorkspace.schemas);
  const requestCreativeHelpFieldName = getSchemaFieldNameByField(
    schemas,
    'requestCreativeHelp'
  );
  const preProductionExternalReviewFieldName = getSchemaFieldNameByField(
    schemas,
    'preProductionExternalReview'
  );
  const postProductionExternalReviewFieldName = getSchemaFieldNameByField(
    schemas,
    'postProductionExternalReview'
  );

  const {
    itemRefId,
    homeWorkspaceRefId,
    itemStatusId,
    itemCreativeStatusId,
    researchModelId,
    contentTypeId,
    subtypeId,
    uniOf,
    evolvingInsights,
    leadAuthorEmpCd,
    isFirstTake,
    isExcludeFromCreativeTokenCount,
    isExcludeFromEditorialTokenCount,
    isProofreadingOnly,
    isBranded
  } = getDataValues(data);
  const isWorkspaceOwner = useSelector(s =>
    isOwner(getAllRoles(s.accessControl, homeWorkspaceRefId))
  );

  const config = useSelector(s =>
    getInheritedContentTypeConfiguration(
      s.planning.contentTypeConfigurations,
      researchModelId,
      contentTypeId,
      subtypeId
    )
  );

  const [collapsed, setCollapsed] = React.useState({ creative: false });
  const [workflowInfo, setWorkflowInfo] = React.useState<WorkflowInfo>(null);
  const [workflowSteps, setWorkflowSteps] =
    React.useState<WorkflowStep[]>(null);
  const [creativeHelpDisabled, setCreativeHelpDisabled] =
    React.useState<boolean>(false);

  const initialConfigureWorkflowSkipIds = React.useMemo(
    () => data.props?.configureWorkflowSkipIds,
    [data]
  );

  const [editorialTokenCount, setEditorialTokenCount] =
    React.useState<number>(null);
  const [creativeTokenCount, setCreativeTokenCount] =
    React.useState<number>(null);
  const [plannedEditorialTokenCount, setPlannedEditorialTokenCount] =
    React.useState<number>(null);
  const [plannedCreativeTokenCount, setPlannedCreativeTokenCount] =
    React.useState<number>(null);

  const { isLoading, Loader, Alert, setLoading, setMessage } = useLoader(
    useAlert({})
  );

  const statusUiConfigById = React.useMemo<WorkflowStageUiConfigById>(
    () => ({
      [ItemStatus.ProofreadingIntake]: {
        tooltipContent:
          'Expedited workflow. Focuses on fixing only egregious errors in spelling, grammar, punctuation and Gartner style.'
      },
      [ItemStatus.InitialEditingIntake]: {
        tooltipContent:
          'Starts with dev editing and provides downstream editing with a focus on utility, consumability and error-free.',
        showEditorialTokenCounter: true
      }
    }),
    []
  );

  const getMainWorkflowType = React.useCallback(
    (skipIds: number[] | null) => {
      if (isBranded) {
        return ConfigureWorkflowType.Branded;
      }
      if (skipIds != null) {
        if (skipIds.includes(ItemStatus.InitialEditingIntake)) {
          const isDefault =
            skipIds.length === defaultSkips.length &&
            defaultSkips.every(id => skipIds.includes(id));
          if (isDefault) {
            return ConfigureWorkflowType.Default;
          }
          return ConfigureWorkflowType.CustomizedProofreading;
        }
        return ConfigureWorkflowType.CustomizedEditing;
      }
      return null;
    },
    [isBranded]
  );

  const getUpdatedSkipIds = React.useCallback(
    (currentIds: number[] | null, statusId: number) => {
      const newIds = [...(currentIds || [])];
      const idx = newIds.indexOf(statusId);
      if (idx > -1) {
        newIds.splice(idx, 1);
        if (
          statusId == ItemStatus.InitialEditingIntake &&
          !newIds.includes(ItemStatus.ProofreadingIntake)
        ) {
          newIds.push(ItemStatus.ProofreadingIntake);
        } else if (
          statusId == ItemStatus.ProofreadingIntake &&
          !newIds.includes(ItemStatus.InitialEditingIntake)
        ) {
          newIds.push(ItemStatus.InitialEditingIntake);
        }
      } else {
        newIds.push(statusId);
        if (
          statusId == ItemStatus.InitialEditingIntake &&
          newIds.includes(ItemStatus.ProofreadingIntake)
        ) {
          newIds.splice(newIds.indexOf(ItemStatus.ProofreadingIntake), 1);
        } else if (
          statusId == ItemStatus.ProofreadingIntake &&
          newIds.includes(ItemStatus.InitialEditingIntake)
        ) {
          newIds.splice(newIds.indexOf(ItemStatus.InitialEditingIntake), 1);
        }
      }
      newIds.sort((a, b) => a - b);
      return newIds;
    },
    []
  );

  const handleWorkflowStageChange = React.useCallback(
    (statusId: number) => {
      const newIds = getUpdatedSkipIds(configureWorkflowSkipIds, statusId);
      const newWorkflowType = getMainWorkflowType(newIds);
      onWorkflowStateChange({
        configureWorkflowSkipIds: newIds,
        configureWorkflowType: newWorkflowType
      });
    },
    [
      configureWorkflowSkipIds,
      getMainWorkflowType,
      getUpdatedSkipIds,
      onWorkflowStateChange
    ]
  );
  const handleRequestCreativeHelpChange = React.useCallback(
    (checked: boolean) => {
      onWorkflowStateChange({ requestCreativeHelp: checked });
    },
    []
  );

  const handlePreProductionExternalReviewChange = React.useCallback(
    (checked: boolean) => {
      onWorkflowStateChange({ preProductionExternalReview: checked });
    },
    []
  );

  const handlePostProductionExternalReviewChange = React.useCallback(
    (checked: boolean) => {
      onWorkflowStateChange({ postProductionExternalReview: checked });
    },
    []
  );

  const showPreProductionExternalReview =
    !!config?.props?.preProductionExternalReview;
  const isPreProductionExternalReviewAutoSelect =
    !!config?.props?.preProductionExternalReviewAutoSelect;

  const showPostProductionExternalReview =
    !!config?.props?.postProductionExternalReview;
  const isPostProductionExternalReviewAutoSelect =
    !!config?.props?.postProductionExternalReviewAutoSelect;

  const {
    canConfigureCreativeHelp,
    canConfigureWorkflow,
    canConfigureExternalReview
  } = useConfigureWorkflowPermissions({
    empCd,
    leadAuthorEmpCd,
    itemCreativeStatusId,
    workflowInfo,
    isBackupTM,
    isAdmin,
    isWorkspaceOwner,
    contentTypeId,
    itemStatusId,
    showExternalReview:
      showPreProductionExternalReview || showPostProductionExternalReview
  });

  const hasChanges = isModified(
    data,
    configureWorkflowSkipIds,
    requestCreativeHelp,
    initialConfigureWorkflowSkipIds,
    preProductionExternalReview,
    postProductionExternalReview
  );

  const configurableExternalReviewNames: string[] = [];
  if (
    showPreProductionExternalReview &&
    !isPreProductionExternalReviewAutoSelect
  )
    configurableExternalReviewNames.push(preProductionExternalReviewFieldName);
  if (
    showPostProductionExternalReview &&
    !isPostProductionExternalReviewAutoSelect
  )
    configurableExternalReviewNames.push(postProductionExternalReviewFieldName);

  const warnings = getWarningMessages(
    isFirstTake,
    isBranded,
    canConfigureWorkflow,
    leadAuthorEmpCd,
    workflowInfo,
    researchModelId,
    contentTypeId,
    config,
    requestCreativeHelpFieldName,
    canConfigureCreativeHelp,
    undefined,
    configurableExternalReviewNames,
    canConfigureExternalReview
  );

  const isWorkflowDisabled =
    warnings.filter(w =>
      [
        ConfigureWorkflowWarningType.WORKFLOW,
        ConfigureWorkflowWarningType.BOTH
      ].includes(w.warningType)
    ).length > 0;

  const isModalDisabled =
    warnings.filter(w =>
      [ConfigureWorkflowWarningType.BOTH].includes(w.warningType)
    ).length > 0;

  React.useEffect(() => {
    setLoading(isSaving);
  }, [isSaving, setLoading]);

  React.useEffect(() => {
    if (errorMessage) setMessage(errorMessage);
  }, [errorMessage, setMessage]);

  React.useEffect(() => {

    const canMakeChange =
      (!isWorkflowDisabled && !creativeHelpDisabled) ||
      canConfigureExternalReview;

    onSaveDisabledChange(
      isLoading ||
        isModalDisabled ||
        (canMakeChange && (!hasChanges || !workflowInfo?.isWipItem))
    );
    onHasChangesChange(hasChanges);
    onIsModalDisabledChange(isModalDisabled);
  }, [
    isLoading,
    isModalDisabled,
    isWorkflowDisabled,
    creativeHelpDisabled,
    canConfigureExternalReview,
    hasChanges,
    workflowInfo,
    onSaveDisabledChange,
    onHasChangesChange,
    onIsModalDisabledChange
  ]);

  React.useEffect(() => {
    if (
      isPreProductionExternalReviewAutoSelect &&
      !preProductionExternalReview
    ) {
      onWorkflowStateChange({
        preProductionExternalReview: true
      });
    }
  }, [
    isPreProductionExternalReviewAutoSelect,
    preProductionExternalReview,
    onWorkflowStateChange
  ]);

  React.useEffect(() => {
    if (
      isPostProductionExternalReviewAutoSelect &&
      !postProductionExternalReview
    ) {
      onWorkflowStateChange({
        postProductionExternalReview: true
      });
    }
  }, [
    isPostProductionExternalReviewAutoSelect,
    postProductionExternalReview,
    onWorkflowStateChange
  ]);

  React.useEffect(() => {
    const { getWorkflowSteps, getWorkflowInfo } = getAPI();

    const isMainWorkflowStatus = (statusId: number) =>
      statusId === ItemStatus.Idea ||
      statusId === ItemStatus.Concept_Review ||
      (statusId >= ItemStatus.Outline &&
        statusId <= ItemStatus.TranslationFinalEdit);

    setLoading(true);
    Promise.all([getWorkflowInfo(itemRefId), getWorkflowSteps()])
      .then(([wInfo, steps]) => {
        if (!config || !steps)
          throw new Error('Invalid configuration or steps');

        setWorkflowInfo(wInfo);

        const skips = getSkipWorkflowStatuses(
          config,
          !!uniOf,
          evolvingInsights,
          isProofreadingOnly
        );
        const newWorkflowSteps = steps.reduce((acc, ws) => {
          ws.statuses = ws.statuses?.filter(s => {
            if (skips.includes(s.statusId)) return false;
            return isMainWorkflowStatus(s.statusId);
          });
          if (ws.statuses?.length > 0) {
            ws.statuses.forEach(s => {
              if (isFirstTake) s.disabled = true;
              if (s.statusId <= itemStatusId) s.disabled = true;
              switch (s.statusId) {
                case ItemStatus.ProofreadingIntake:
                  if (isProofreadingOnly) s.disabled = true;
                  break;
                case ItemStatus.Draft:
                case ItemStatus.DraftPeerReview:
                  s.disabled = true;
                  break;
              }
            });
            if (isFirstTake) {
              ws.statuses = ws.statuses.filter(s =>
                [
                  ItemStatus.Draft,
                  ItemStatus.ProofreadingIntake,
                  ItemStatus.TeamManagerSignoff
                ].includes(s.statusId)
              );
            }
            if (ws.statuses.length > 0) {
              acc.push(ws);
            }
          }
          return acc;
        }, []);
        setWorkflowSteps(newWorkflowSteps);

        setCreativeHelpDisabled(
          getCreativeHelpDisabled(
            wInfo,
            itemCreativeStatusId,
            data,
            config,
            itemStatusId,
            false
          )
        );

        onWorkflowStateChange({
          requestCreativeHelp:
            typeof data?.props?.requestCreativeHelp == 'boolean'
              ? data.props.requestCreativeHelp
              : config?.props?.creativeHelpAutoEmail || false
        });
      })
      .catch(err => {
        console.error('Failed to load workflow steps/configurations', err);
        setMessage('Failed to load workflow steps/configurations');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    researchModelId,
    contentTypeId,
    subtypeId,
    uniOf,
    isFirstTake,
    isProofreadingOnly,
    leadAuthorEmpCd,
    itemStatusId,
    itemRefId
  ]);

  React.useEffect(() => {
    if (isBranded) {
      onWorkflowStateChange({
        configureWorkflowType: ConfigureWorkflowType.Branded
      });
      onWorkflowStateChange({ configureWorkflowSkipIds: null });
    } else {
      onWorkflowStateChange({
        configureWorkflowType: getMainWorkflowType(configureWorkflowSkipIds)
      });
    }
  }, [configureWorkflowSkipIds, getMainWorkflowType, isBranded]);

  React.useEffect(() => {
    if (
      configureWorkflowSkipIds == null &&
      workflowSteps != null &&
      !isBranded
    ) {
      onWorkflowStateChange({ configureWorkflowSkipIds: defaultSkips });
    }
  }, [configureWorkflowSkipIds, workflowSteps, isBranded]);

  React.useEffect(() => {
    const shouldAddCreativeToken =
      !!workflowInfo?.hasNotCompletedCreative &&
      requestCreativeHelp &&
      !isExcludeFromCreativeTokenCount;

    if (typeof workflowInfo?.allocatedCreativeWorkflowCount == 'number') {
      let newCount = workflowInfo.allocatedCreativeWorkflowCount;
      if (
        shouldAddCreativeToken &&
        itemCreativeStatusId?.props?.id > CreativeStatus.WaitingForDesign &&
        itemCreativeStatusId?.props?.id <=
          CreativeStatus.CreativeAdditionalCreativeDesignReview
      )
        newCount += 1;
      setCreativeTokenCount(newCount);
    }

    if (typeof workflowInfo?.plannedCreativeWorkflowCount == 'number') {
      let newCount = workflowInfo.plannedCreativeWorkflowCount;
      if (
        shouldAddCreativeToken &&
        (!itemCreativeStatusId ||
          itemCreativeStatusId?.props?.id <= CreativeStatus.WaitingForDesign)
      ) {
        newCount += 1;
      }
      setPlannedCreativeTokenCount(newCount);
    }
  }, [requestCreativeHelp, workflowInfo, isExcludeFromCreativeTokenCount]);

  React.useEffect(() => {
    const shouldAddEditorialToken =
      !isExcludeFromEditorialTokenCount &&
      !isBranded &&
      workflowInfo?.isWipItem;
    if (typeof workflowInfo?.allocatedEditorialWorkflowCount == 'number') {
      let newCount = workflowInfo.allocatedEditorialWorkflowCount;
      if (
        shouldAddEditorialToken &&
        !configureWorkflowSkipIds?.includes(ItemStatus.InitialEditingIntake) &&
        itemStatusId > ItemStatus.WaitingforEditing &&
        itemStatusId <= ItemStatus.FinalEditorReview
      ) {
        newCount += 1;
      }
      setEditorialTokenCount(newCount);
    }

    if (typeof workflowInfo?.plannedEditorialWorkflowCount == 'number') {
      let newCount = workflowInfo.plannedEditorialWorkflowCount;
      if (
        shouldAddEditorialToken &&
        !configureWorkflowSkipIds?.includes(ItemStatus.InitialEditingIntake) &&
        itemStatusId <= ItemStatus.WaitingforEditing
      ) {
        newCount += 1;
      }
      setPlannedEditorialTokenCount(newCount);
    }
  }, [
    configureWorkflowSkipIds,
    workflowInfo,
    isBranded,
    isExcludeFromEditorialTokenCount
  ]);

  return (
    <div className="slider-activator">
      <div className="workflow-modal-body">
        {warnings.map(w => (
          <Warning key={w.message}>{w.message}</Warning>
        ))}
        <Loader />
        <Alert />
        {configureWorkflowType && (
          <div>
            <label>Selected Workflow Type:&nbsp;</label>
            <b>{typeEnums[configureWorkflowType] || configureWorkflowType}</b>
          </div>
        )}
        <ConfigureWorkflowTree
          workflowSteps={workflowSteps}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          configureWorkflowSkipIds={configureWorkflowSkipIds}
          onWorkflowStageChange={handleWorkflowStageChange}
          isLoading={isLoading}
          isWorkflowDisabled={isWorkflowDisabled}
          workflowInfo={workflowInfo}
          editorialTokenCount={editorialTokenCount}
          plannedEditorialTokenCount={plannedEditorialTokenCount}
          creativeHelpDisabled={creativeHelpDisabled}
          canConfigureCreativeHelp={canConfigureCreativeHelp}
          isModalDisabled={isModalDisabled}
          requestCreativeHelp={requestCreativeHelp}
          onRequestCreativeHelpChange={handleRequestCreativeHelpChange}
          creativeTokenCount={creativeTokenCount}
          plannedCreativeTokenCount={plannedCreativeTokenCount}
          statusUiConfigById={statusUiConfigById}
          requestCreativeHelpFieldName={requestCreativeHelpFieldName}
          preProductionExternalReview={preProductionExternalReview}
          onPreProductionExternalReviewChange={
            handlePreProductionExternalReviewChange
          }
          showPreProductionExternalReview={showPreProductionExternalReview}
          isPreProductionExternalReviewAutoSelect={
            isPreProductionExternalReviewAutoSelect
          }
          itemStatusId={itemStatusId}
          postProductionExternalReview={postProductionExternalReview}
          onPostProductionExternalReviewChange={
            handlePostProductionExternalReviewChange
          }
          showPostProductionExternalReview={showPostProductionExternalReview}
          isPostProductionExternalReviewAutoSelect={
            isPostProductionExternalReviewAutoSelect
          }
          preProductionExternalReviewFieldName={
            preProductionExternalReviewFieldName
          }
          postProductionExternalReviewFieldName={
            postProductionExternalReviewFieldName
          }
          canConfigureExternalReview={canConfigureExternalReview}
        />
      </div>
    </div>
  );
};
